import { Loader2, Mail, Bell } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useUserPreferences, useUpdateUserPreferences } from '@/hooks/use-user-preferences';
import { toast } from '@/components/ui/toaster';

export function EmailPreferencesCard() {
  const { data: preferences, isLoading, error } = useUserPreferences();
  const updatePreferences = useUpdateUserPreferences();

  const handleToggle = async (enabled: boolean) => {
    try {
      await updatePreferences.mutateAsync({ emailNotificationsEnabled: enabled });
      toast({
        title: 'Preferences updated',
        description: enabled
          ? 'You will now receive email notifications.'
          : 'Email notifications have been disabled.',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update preferences.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-6">
          <p className="text-sm text-muted-foreground">
            Failed to load email preferences.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Email Notifications</CardTitle>
        </div>
        <CardDescription>
          Control what email notifications you receive from WeTicket.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Bell className="mt-0.5 h-5 w-5 text-muted-foreground" />
            <div className="space-y-1">
              <Label htmlFor="email-notifications" className="font-medium">
                Email Notifications
              </Label>
              <p className="text-sm text-muted-foreground">
                Receive email notifications for task assignments and important updates.
              </p>
            </div>
          </div>
          <Switch
            id="email-notifications"
            checked={preferences?.emailNotificationsEnabled ?? true}
            onCheckedChange={handleToggle}
            disabled={updatePreferences.isPending}
          />
        </div>

        <div className="rounded-md bg-muted/50 p-4">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Note:</strong> Password reset and invite emails
            are always sent regardless of this setting.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
